import type {
  SummaryData,
  DailyUsage,
  TopAction,
  UserActivity,
  HourlyDistribution,
  FeatureAdoption,
  AdoptionRate,
  UserSegmentation,
  TimeSaved,
  FeatureGap,
  UsageBreakdownData,
  ActiveAccountAdoption,
  TenantUser,
  TenantRole,
} from '../types';
import { getHostRole } from './authConfig';
import { getFirebaseAuth } from './firebase';

const API_BASE_URL = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public userMessage: string,
    public statusCode?: number,
    /** Machine-readable code from a `{"error": "..."}` body (e.g. 'last_admin'). */
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * `fetch` wrapper that attaches a fresh Firebase ID token as a Bearer header.
 * In dev (no auth) it issues a bare same-origin request. The token is re-minted
 * per request via `getIdToken()` (returns the cached value while fresh, rotates
 * internally) - never cached here. No cookies, no redirects: a 401/403 surfaces
 * as an `ApiError` and the UI shows the appropriate screen.
 */
export async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  if (getHostRole() === 'dev') {
    return fetch(url, init)
  }
  const auth = getFirebaseAuth()
  const user = auth?.currentUser
  if (!user) {
    throw new ApiError('not signed in', 'Authentication Required: Please sign in.', 401)
  }
  const token = await user.getIdToken()
  return fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  })
}

class ApiService {
  private getErrorMessage(endpoint: string, status: number): { title: string; message: string } {
    // Network/Connection errors
    if (status === 0) {
      return {
        title: 'Connection Failed',
        message: 'Could not connect to the server. Please check your internet connection.',
      }
    }

    // Client errors (4xx)
    if (status >= 400 && status < 500) {
      if (status === 401) {
        return {
          title: 'Authentication Required',
          message: 'Your session has expired. Please refresh the page and try again.',
        }
      }
      if (status === 403) {
        return {
          title: 'Access Denied',
          message: 'You do not have permission to access this data.',
        }
      }
      if (status === 404) {
        return {
          title: 'Data Not Found',
          message: 'The requested data could not be found. It may have been removed or is not yet available.',
        }
      }
      if (status === 429) {
        return {
          title: 'Too Many Requests',
          message: 'You are making too many requests. Please wait a moment and try again.',
        }
      }
      return {
        title: 'Invalid Request',
        message: 'The request could not be processed. Please check your input and try again.',
      }
    }

    // Server errors (5xx)
    if (status >= 500) {
      if (status === 503) {
        return {
          title: 'Service Unavailable',
          message: 'The server is temporarily unavailable. Please try again in a few moments.',
        }
      }
      return {
        title: 'Server Error',
        message: 'An error occurred on the server while loading the data. Please try again later.',
      }
    }

    // Unknown error
    return {
      title: 'Unexpected Error',
      message: 'An unexpected error occurred. Please refresh the page and try again.',
    }
  }

  private async fetchData<T>(endpoint: string): Promise<T> {
    try {
      const response = await authedFetch(`${API_BASE_URL}${endpoint}`)

      if (!response.ok) {
        const errorInfo = this.getErrorMessage(endpoint, response.status)
        throw new ApiError(
          `HTTP error! status: ${response.status}`,
          `${errorInfo.title}: ${errorInfo.message}`,
          response.status
        )
      }

      return await response.json()
    } catch (error) {
      // Network error or JSON parsing error
      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(
          'Network error',
          'Could not connect to the server. Please check your internet connection.',
          0
        )
      }

      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error',
        'An unexpected error occurred while loading data. Please try again.',
        0
      )
    }
  }

  /**
   * Request helper for the user-management endpoints. Unlike `fetchData` it
   * supports non-GET verbs and parses the `{"error": "<code>"}` body so callers
   * can branch on the code (wrong_domain / already_exists / last_admin).
   */
  private async adminRequest<T>(endpoint: string, init?: RequestInit): Promise<T> {
    let response: Response
    try {
      response = await authedFetch(`${API_BASE_URL}${endpoint}`, {
        ...init,
        headers: {
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(init?.headers ?? {}),
        },
      })
    } catch (error) {
      if (error instanceof ApiError) throw error
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        'Connection Failed: Could not connect to the server. Please check your internet connection.',
        0
      )
    }

    if (!response.ok) {
      let code: string | undefined
      try {
        const body = await response.json()
        if (body && typeof body.error === 'string') code = body.error
      } catch {
        // no JSON body; fall back to the status-based message
      }
      const errorInfo = this.getErrorMessage(endpoint, response.status)
      throw new ApiError(
        `HTTP error! status: ${response.status}`,
        `${errorInfo.title}: ${errorInfo.message}`,
        response.status,
        code
      )
    }

    return await response.json()
  }

  /** Tenant members. Admin-only: throws ApiError(403) for a plain user. */
  async listUsers(): Promise<TenantUser[]> {
    const data = await this.adminRequest<{ users: TenantUser[] }>('/users')
    return data.users ?? []
  }

  async addUser(email: string, role: TenantRole): Promise<TenantUser> {
    const data = await this.adminRequest<{ user: TenantUser }>('/users', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
    return data.user
  }

  async updateUser(
    email: string,
    patch: { role?: TenantRole; disabled?: boolean }
  ): Promise<TenantUser> {
    const data = await this.adminRequest<{ user: TenantUser }>(
      `/users/${encodeURIComponent(email)}`,
      { method: 'PATCH', body: JSON.stringify(patch) }
    )
    return data.user
  }

  async deleteUser(email: string): Promise<void> {
    await this.adminRequest<{ ok: boolean }>(`/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    })
  }

  async getSummary(): Promise<SummaryData> {
    return this.fetchData<SummaryData>('/summary');
  }

  async getDailyUsage(): Promise<DailyUsage[]> {
    return this.fetchData<DailyUsage[]>('/daily-usage');
  }

  async getTopActions(limit: number = 10, period: string = 'week'): Promise<TopAction[]> {
    return this.fetchData<TopAction[]>(`/top-actions?limit=${limit}&period=${period}`);
  }

  async getUserActivity(limit: number = 15, period: string = 'week'): Promise<UserActivity[]> {
    return this.fetchData<UserActivity[]>(`/user-activity?limit=${limit}&period=${period}`);
  }

  async getHourlyDistribution(): Promise<HourlyDistribution[]> {
    return this.fetchData<HourlyDistribution[]>('/hourly-distribution');
  }

  async getFeatureAdoption(): Promise<FeatureAdoption[]> {
    return this.fetchData<FeatureAdoption[]>('/feature-adoption');
  }

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.fetchData('/health');
  }

  /** Authorization probe. 200 = authorized; throws ApiError (401/403) otherwise. */
  async getMe(): Promise<{
    authenticated: boolean
    email?: string
    slug?: string
    role?: TenantRole
    platform_admin?: boolean
    dev?: boolean
  }> {
    return this.fetchData('/auth/me');
  }

  async getAdoptionRate(period: string = 'week'): Promise<AdoptionRate> {
    return this.fetchData<AdoptionRate>(`/adoption-rate?period=${period}`);
  }

  async getUserSegmentation(period: string = 'week'): Promise<UserSegmentation> {
    return this.fetchData<UserSegmentation>(`/user-segmentation?period=${period}`);
  }

  async getTimeSaved(period: string = 'week'): Promise<TimeSaved> {
    return this.fetchData<TimeSaved>(`/time-saved?period=${period}`);
  }

  async getFeatureGaps(period: string = 'week'): Promise<FeatureGap[]> {
    return this.fetchData<FeatureGap[]>(`/feature-gaps?period=${period}`);
  }

  async getUsageBreakdown(period: string = 'week'): Promise<UsageBreakdownData> {
    return this.fetchData<UsageBreakdownData>(`/usage-breakdown?period=${period}`);
  }

  async getTimeSeries(startDate: string, endDate: string, granularity: 'hourly' | 'daily' = 'daily'): Promise<any[]> {
    return this.fetchData<any[]>(`/time-series?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`);
  }

  async getTimeSeriesBreakdown(startDate: string, endDate: string, granularity: 'hourly' | 'daily' = 'daily', breakdownBy: string = 'action', userEmail: string = ''): Promise<any[]> {
    let url = `/time-series-breakdown?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}&breakdown_by=${breakdownBy}`;
    if (userEmail) {
      url += `&user_email=${encodeURIComponent(userEmail)}`;
    }
    return this.fetchData<any[]>(url);
  }

  async getAdoptionRateByRange(startDate: string, endDate: string): Promise<AdoptionRate> {
    return this.fetchData<AdoptionRate>(`/adoption-rate-range?start_date=${startDate}&end_date=${endDate}`);
  }

  async getUsageBreakdownByRange(startDate: string, endDate: string, userEmail: string = ''): Promise<UsageBreakdownData> {
    let url = `/usage-breakdown-range?start_date=${startDate}&end_date=${endDate}`;
    if (userEmail) {
      url += `&user_email=${encodeURIComponent(userEmail)}`;
    }
    return this.fetchData<UsageBreakdownData>(url);
  }

  async getUserSegmentationByRange(startDate: string, endDate: string, apps?: string[], actions?: string[]): Promise<UserSegmentation> {
    let url = `/user-segmentation-range?start_date=${startDate}&end_date=${endDate}`;
    if (apps && apps.length > 0) {
      url += `&apps=${apps.join(',')}`;
    }
    if (actions && actions.length > 0) {
      url += `&actions=${actions.join(',')}`;
    }
    return this.fetchData<UserSegmentation>(url);
  }

  async getActiveAccountAdoption(startDate: string, endDate: string, inactiveDays: number = 30): Promise<ActiveAccountAdoption> {
    return this.fetchData<ActiveAccountAdoption>(
      `/active-account-adoption?start_date=${startDate}&end_date=${endDate}&inactive_days=${inactiveDays}`
    );
  }

  async getTimeSavedByRange(startDate: string, endDate: string): Promise<TimeSaved> {
    return this.fetchData<TimeSaved>(`/time-saved-range?start_date=${startDate}&end_date=${endDate}`);
  }

  async getFeatureGapsByRange(startDate: string, endDate: string): Promise<FeatureGap[]> {
    return this.fetchData<FeatureGap[]>(`/feature-gaps-range?start_date=${startDate}&end_date=${endDate}`);
  }

  async getUserActivityByRange(startDate: string, endDate: string, limit: number = 100, apps?: string[], actions?: string[]): Promise<any[]> {
    let url = `/user-activity-range?start_date=${startDate}&end_date=${endDate}&limit=${limit}`;
    if (apps && apps.length > 0) {
      url += `&apps=${apps.join(',')}`;
    }
    if (actions && actions.length > 0) {
      url += `&actions=${actions.join(',')}`;
    }
    return this.fetchData<any[]>(url);
  }

  async getAvailableFilters(startDate: string, endDate: string, selectedApps?: string[], selectedActions?: string[]): Promise<{apps: string[], actions: string[]}> {
    let url = `/available-filters?start_date=${startDate}&end_date=${endDate}`;
    if (selectedApps && selectedApps.length > 0) {
      url += `&apps=${selectedApps.join(',')}`;
    }
    if (selectedActions && selectedActions.length > 0) {
      url += `&actions=${selectedActions.join(',')}`;
    }
    return this.fetchData<{apps: string[], actions: string[]}>(url);
  }

  async getSegmentationTimeSeries(startDate: string, endDate: string, apps?: string[], actions?: string[]): Promise<any[]> {
    let url = `/segmentation-time-series?start_date=${startDate}&end_date=${endDate}`;
    if (apps && apps.length > 0) {
      url += `&apps=${apps.join(',')}`;
    }
    if (actions && actions.length > 0) {
      url += `&actions=${actions.join(',')}`;
    }
    return this.fetchData<any[]>(url);
  }
}

export const apiService = new ApiService();

// User-management API, also exposed as plain functions for the admin screen.
export const listUsers = () => apiService.listUsers()
export const addUser = (email: string, role: TenantRole) => apiService.addUser(email, role)
export const updateUser = (email: string, patch: { role?: TenantRole; disabled?: boolean }) =>
  apiService.updateUser(email, patch)
export const deleteUser = (email: string) => apiService.deleteUser(email)
